import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "ush_admin_mode_v1";

// Modes
export const MODE_AGENT = "agent";
export const MODE_FULL = "full";
export const MODE_PRESIDENT = "president";

// Passcodes
const FULL_PASSCODE = "111";
const PRESIDENT_PASSCODE = "222";

const AdminModeContext = createContext({
  mode: MODE_AGENT,
  unlock: () => false,
  lock: () => {},
  isFull: false,
  isPresident: false,
  expiresAt: null,
});

/** Next Thursday at 00:00 in the user's local timezone, as a JS timestamp.
 * If today IS Thursday and it's before 00:00 (i.e. never), we still return
 * 7 days out; otherwise we return the upcoming Thursday. */
function nextThursdayTimestamp(from = new Date()) {
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 Sun .. 4 Thu .. 6 Sat
  // Days until next Thursday (always strictly in the future — 7 days if today is Thu)
  const daysAhead = ((4 - day + 7) % 7) || 7;
  d.setDate(d.getDate() + daysAhead);
  return d.getTime();
}

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_err) { void _err; /* corrupt or unavailable storage — fall back to default mode */ return null; }
}

function persist(state) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (_err) { void _err; /* localStorage disabled (private window, etc.) — mode just won't survive a reload */ }
}

export function AdminModeProvider({ children }) {
  const [state, setState] = useState(() => {
    const stored = loadStored();
    if (!stored) return { mode: MODE_AGENT, expiresAt: null };
    if (stored.mode === MODE_PRESIDENT) return { mode: MODE_PRESIDENT, expiresAt: null };
    if (stored.mode === MODE_FULL) {
      if (stored.expiresAt && Date.now() < stored.expiresAt) return stored;
      // Expired — fall back to agent.
      persist(null);
      return { mode: MODE_AGENT, expiresAt: null };
    }
    return { mode: MODE_AGENT, expiresAt: null };
  });

  // Re-check expiry every minute so the UI lapses back to agent mode when
  // the 7-day window closes without needing a manual refresh.
  useEffect(() => {
    if (state.mode !== MODE_FULL || !state.expiresAt) return;
    const tick = () => {
      if (Date.now() >= state.expiresAt) {
        setState({ mode: MODE_AGENT, expiresAt: null });
        persist(null);
      }
    };
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [state.mode, state.expiresAt]);

  const unlock = useCallback((passcode) => {
    const p = String(passcode || "").trim();
    if (p === FULL_PASSCODE) {
      const next = { mode: MODE_FULL, expiresAt: nextThursdayTimestamp() };
      setState(next);
      persist(next);
      return MODE_FULL;
    }
    if (p === PRESIDENT_PASSCODE) {
      const next = { mode: MODE_PRESIDENT, expiresAt: null };
      setState(next);
      persist(next);
      return MODE_PRESIDENT;
    }
    return false;
  }, []);

  const lock = useCallback(() => {
    setState({ mode: MODE_AGENT, expiresAt: null });
    persist(null);
  }, []);

  const value = useMemo(() => ({
    mode: state.mode,
    expiresAt: state.expiresAt,
    isAgent: state.mode === MODE_AGENT,
    isFull: state.mode === MODE_FULL || state.mode === MODE_PRESIDENT,
    isPresident: state.mode === MODE_PRESIDENT,
    unlock,
    lock,
  }), [state, unlock, lock]);

  return <AdminModeContext.Provider value={value}>{children}</AdminModeContext.Provider>;
}

export const useAdminMode = () => useContext(AdminModeContext);
